import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { fetchEffectiveOnce } from '../services/uiSettingsService';
import { safeStorage } from '../utils/safeStorage';

export interface UiVisibility {
  showApiStatus: boolean;
  showDebugMenu: boolean;
  showLanguageSelector: boolean;
  debugMenuUsernames: string[];
  showRawTab: boolean;
  rawTabUsernames: string[];
  showJsonConfig: boolean;
  jsonConfigUsernames: string[];
  ready: boolean;
}


const DEFAULT_VISIBILITY: Omit<UiVisibility, 'ready'> = {
  showApiStatus: true,
  showDebugMenu: true,
  showLanguageSelector: true,
  debugMenuUsernames: [],
  showRawTab: true,
  rawTabUsernames: [],
  showJsonConfig: true,
  jsonConfigUsernames: [],
};

const UiVisibilityContext = createContext<UiVisibility>({ ...DEFAULT_VISIBILITY, ready: true });


const hasAccessToken = (): boolean => {
  try {
    return !!safeStorage.getItem('access_token');
  } catch {
    return false;
  }
};

export const UiVisibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visibility, setVisibility] = useState<UiVisibility>(() => ({
    ...DEFAULT_VISIBILITY,
    ready: !hasAccessToken(),
  }));

  useEffect(() => {
    let cancelled = false;

    const applyServerDefaults = async () => {
      if (cancelled) return;
      try {
        const eff = await fetchEffectiveOnce();
        if (cancelled) return;
        const s = eff?.settings || {};
        setVisibility({
          showApiStatus: typeof s.show_api_status === 'boolean' ? s.show_api_status : true,
          showDebugMenu: typeof s.show_debug_menu === 'boolean' ? s.show_debug_menu : true,
          showLanguageSelector:
            typeof s.show_language_selector === 'boolean' ? s.show_language_selector : true,
          debugMenuUsernames: Array.isArray(s.debug_menu_usernames)
            ? s.debug_menu_usernames.filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [],
          showRawTab: typeof s.show_raw_tab === 'boolean' ? s.show_raw_tab : true,
          rawTabUsernames: Array.isArray(s.raw_tab_usernames)
            ? s.raw_tab_usernames.filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [],
          showJsonConfig: typeof s.show_json_config === 'boolean' ? s.show_json_config : true,
          jsonConfigUsernames: Array.isArray(s.json_config_usernames)
            ? s.json_config_usernames.filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [],
          ready: true,
        });
      } catch {
        // Silent: keep built-in defaults but still flip ready so the header
        // unblocks.
        if (!cancelled) setVisibility(v => ({ ...v, ready: true }));
      }
    };

    applyServerDefaults();
    const listener = () => applyServerDefaults();
    window.addEventListener('app:auth-ready', listener);
    return () => {
      cancelled = true;
      window.removeEventListener('app:auth-ready', listener);
    };
  }, []);

  return (
    <UiVisibilityContext.Provider value={visibility}>
      {children}
    </UiVisibilityContext.Provider>
  );
};

export const useUiVisibility = (): UiVisibility => useContext(UiVisibilityContext);
